<template>
  <v-card flat>
    <v-card-text>
      <!-- Barra de herramientas -->
      <v-toolbar flat>
        <v-toolbar-title>Plantillas de pagos</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="loadTemplates"
          :loading="loading"
          icon
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Tabla de plantillas -->
      <v-data-table
        :headers="headers"
        :items="templates"
        :loading="loading"
        class="elevation-1 mt-4"
        :items-per-page="10"
        :sort-by="['SendNumber']"
        :sort-desc="[true]"
        :footer-props="{
          'items-per-page-options': [5, 10, 15, 20, 50]
        }"
      >
        <template v-slot:[`item.CreatedAt`]="{ item }">
          {{ formatDateTime(item.CreatedAt) }}
        </template>

        <template v-slot:[`item.SendNumber`]="{ item }">
          {{ formatSendNumber(item.SendNumber) }}
        </template>

        <template v-slot:[`item.TotalAmount`]="{ item }">
          ${{ parseFloat(item.TotalAmount || 0).toFixed(2) }}
        </template>

        <template v-slot:[`item.StatusPayment`]="{ item }">
          <v-chip
            small
            :color="item.StatusPayment == 1 ? 'success' : 'warning'"
            outlined
          >
            <v-icon small left>{{ item.StatusPayment == 1 ? 'mdi-check-circle' : 'mdi-clock-outline' }}</v-icon>
            {{ item.StatusPayment == 1 ? 'Procesada' : 'Pendiente' }}
          </v-chip>
        </template>

        <template v-slot:[`item.PaymentCount`]="{ item }">
          <v-chip small color="primary" outlined>
            {{ item.PaymentCount }} pago(s)
          </v-chip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="info"
                v-bind="attrs"
                v-on="on"
                @click="viewTemplate(item)"
              >
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
            </template>
            <span>Ver detalles</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="item.StatusPayment != 1 ? on : {}">
                <v-btn
                  icon
                  small
                  color="primary"
                  :disabled="item.StatusPayment == 1"
                  @click="$emit('edit-template', item.ID)"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </span>
            </template>
            <span>{{ item.StatusPayment == 1 ? 'No se puede editar una plantilla procesada' : 'Editar plantilla' }}</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
              <span v-bind="tooltipAttrs" v-on="item.StatusPayment != 1 ? tooltipOn : {}">
                <v-menu offset-y :disabled="item.StatusPayment != 1">
                  <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                    <v-btn
                      icon
                      small
                      :color="item.StatusPayment == 1 ? 'success' : 'grey'"
                      v-bind="menuAttrs"
                      v-on="item.StatusPayment == 1 ? menuOn : {}"
                      :disabled="item.StatusPayment != 1"
                    >
                      <v-icon small>mdi-download</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item @click="exportToExcel(item)">
                      <v-list-item-icon>
                        <v-icon  color="teal" size="32" >mdi-file-excel</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Exportar EXCEL</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="generatePNR(item)" >
                      <v-list-item-icon>
                        <v-icon color="success" size="32">mdi-file-download</v-icon>
                      </v-list-item-icon>
                        <v-list-item-title>Descargar PRN</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </span>
            </template>
            <span>{{ item.StatusPayment != 1 ? 'La plantilla debe estar procesada para descargar' : 'Descargar archivos' }}</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                :color="item.StatusPayment == 1 ? 'grey' : 'error'"
                v-bind="attrs"
                v-on="on"
                @click="deleteTemplate(item)"
                :disabled="item.StatusPayment == 1"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>{{ item.StatusPayment == 1 ? 'No se puede eliminar una plantilla procesada' : 'Eliminar' }}</span>
          </v-tooltip>
        </template>

        <template v-slot:no-data>
          <v-alert type="info" outlined class="ma-4">
            No hay plantillas de pago guardadas. Cree una nueva desde la pestaña "Crear Plantilla".
          </v-alert>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- Dialog para ver detalles -->
    <v-dialog v-model="detailsDialog" max-width="1200">
      <v-card v-if="selectedTemplate" class="payment-dialog__card">
        <v-card-title class="headline d-flex align-center flex-wrap">
          <span>Plan {{ selectedTemplate.PlanNumber }}</span>
          <v-spacer></v-spacer>
          <div class="d-flex align-center flex-wrap justify-end payment-edit-actions">
            <v-btn
              v-if="!editMode && selectedTemplate.StatusPayment != 1"
              color="primary"
              small
              @click="enableEditMode"
            >
              <v-icon left small>mdi-pencil</v-icon>
              Editar envio
            </v-btn>
            <v-alert
              v-if="!editMode && selectedTemplate.StatusPayment == 1"
              dense
              type="info"
              text
              class="ma-0"
            >
              No se puede editar una plantilla procesada
            </v-alert>
            <div v-if="editMode" class="d-flex align-center flex-wrap">
              <v-btn
                color="success"
                small
                class="mr-2"
                @click="saveChanges"
                :loading="savingChanges"
              >
                <v-icon left small>mdi-content-save</v-icon>
                Guardar
              </v-btn>
              <v-btn
                color="grey"
                small
                text
                @click="cancelEdit"
                :disabled="savingChanges"
              >
                Cancelar
              </v-btn>
            </div>
          </div>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="12" md="6">
              <p><strong>Número de Plan:</strong> {{ selectedTemplate.PlanNumber }}</p>
            </v-col>
            <v-col cols="12" md="6">
              <p><strong>Número de Envío:</strong> {{ formatSendNumber(selectedTemplate.SendNumber) }}</p>
            </v-col>
            <v-col cols="12" md="6">
              <div v-if="editMode && selectedTemplate.StatusPayment != 1" class="payment-date-row">
                <div class="payment-date-content">
                  <div v-if="!modifyDate" class="payment-date-value">
                    <strong>Fecha:</strong> {{ formatDate(selectedTemplate.Date) }}
                  </div>

                  <v-text-field
                    v-else
                    v-model="editableTemplateDate"
                    label="Fecha"
                    type="date"
                    dense
                    outlined
                    hide-details
                    class="ma-0 payment-date-input"
                    :disabled="savingChanges"
                  ></v-text-field>
                </div>

                <v-checkbox
                  v-model="modifyDate"
                  label="Modificar Fecha"
                  dense
                  hide-details
                  class="ma-0 payment-edit-checkbox"
                  :disabled="savingChanges"
                  @change="syncEditableTemplateDate"
                ></v-checkbox>
              </div>
              <p v-else class="mb-0"><strong>Fecha:</strong> {{ formatDate(selectedTemplate.Date) }}</p>
            </v-col>
            <v-col cols="12" md="6">
              <p><strong>Total:</strong> ${{ parseFloat(selectedTemplate.TotalAmount || 0).toFixed(2) }}</p>
            </v-col>
            <v-col cols="12" v-if="selectedTemplate.Name">
              <p><strong>Nombre:</strong> {{ selectedTemplate.Name }}</p>
            </v-col>
            <v-col cols="12" v-if="selectedTemplate.Description">
              <p><strong>Descripción:</strong> {{ selectedTemplate.Description }}</p>
            </v-col>
            <v-col cols="12">
              <p><strong>Cantidad de pagos:</strong> {{ selectedTemplate.PaymentCount }}</p>
            </v-col>
            <v-col cols="12">
              <v-chip
                :color="selectedTemplate.StatusPayment == 1 ? 'success' : 'warning'"
                outlined
                class="mt-2"
              >
                <v-icon small left>{{ selectedTemplate.StatusPayment == 1 ? 'mdi-check-circle' : 'mdi-clock-outline' }}</v-icon>
                <strong>Estado:</strong>&nbsp;{{ selectedTemplate.StatusPayment == 1 ? 'Procesada' : 'Pendiente de Procesar' }}
              </v-chip>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <v-card outlined class="mb-4">
            <v-card-text class="pa-4">
              <div class="text-subtitle-1 font-weight-bold mb-4">Partida Contable</div>
              <v-row align="center">
                <v-col cols="12" md="5">
                  <v-select
                    v-if="!selectedTemplate.ID_Accounting_item"
                    v-model="selectedAccountingMode"
                    :items="accountingModeOptions"
                    item-text="text"
                    item-value="value"
                    label="Tipo de partida"
                    dense
                    outlined
                    hide-details
                    :disabled="selectedTemplate.StatusPayment != 1"
                  ></v-select>
                  <v-btn
                    v-else
                    color="success"
                    outlined
                    @click="openAccountingEntryDetail"
                  >
                    <v-icon left small>mdi-book-check</v-icon>
                    Partida: {{ selectedTemplate.ID_Accounting_item }}
                  </v-btn>
                </v-col>
                <v-col cols="12" md="4">
                  <v-btn
                    v-if="!selectedTemplate.ID_Accounting_item"
                    color="primary"
                    block
                    :disabled="selectedTemplate.StatusPayment != 1"
                    @click="openAccountingPreview"
                  >
                    <v-icon left small>mdi-eye</v-icon>
                    Ver vista previa
                  </v-btn>
                </v-col>
                <v-col cols="12" v-if="!selectedTemplate.ID_Accounting_item && selectedTemplate.StatusPayment != 1">
                  <v-alert dense type="info" outlined class="mb-0">
                    La plantilla debe estar procesada antes de generar su partida contable.
                  </v-alert>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Resumen de totales -->
          <v-card outlined class="mb-4">
            <v-card-text class="pa-4">
              <v-row>
                <v-col cols="12" md="3">
                  <div class="text-subtitle-2 grey--text">Total Original</div>
                  <div class="text-h6">${{ getTemplateOriginalTotal().toFixed(2) }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-subtitle-2 grey--text">Total a Pagar (PRN)</div>
                  <div class="text-h6 success--text">${{ getTemplatePartialTotal().toFixed(2) }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-subtitle-2 grey--text">Saldo Pendiente</div>
                  <div class="text-h6 orange--text">${{ getTemplateBalanceTotal().toFixed(2) }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-subtitle-2 grey--text">Transacciones</div>
                  <div class="text-h6">{{ templatePayments.length }}</div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <h3 class="mb-3">Pagos Incluidos:</h3>

          <div style="max-height: 400px; overflow-y: auto;">
            <v-data-table
              :headers="paymentHeaders"
              :items="templatePayments"
              :loading="loadingPayments"
              dense
              hide-default-footer
              :items-per-page="-1"
            >
              <template v-slot:[`item.Total`]="{ item }">
                ${{ parseFloat(item.Total || 0).toFixed(2) }}
              </template>

              <template v-slot:[`item.PartialAmount`]="{ item }">
                <v-text-field
                  v-if="editMode"
                  v-model.number="item.PartialAmount"
                  type="number"
                  step="0.01"
                  dense
                  outlined
                  hide-details
                  prefix="$"
                  :disabled="savingChanges"
                  @input="updatePaymentBalance(item)"
                  style="max-width: 130px;"
                ></v-text-field>
                <span v-else>
                  ${{ (parseFloat(item.PartialAmount) || parseFloat(item.Total || 0)).toFixed(2) }}
                </span>
              </template>

              <template v-slot:[`item.Balance`]="{ item }">
                <span :class="getPaymentBalanceClass(item)">
                  ${{ calculatePaymentBalance(item).toFixed(2) }}
                </span>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="selectedTemplate.StatusPayment != 1"
            color="success"
            @click="markTemplateAsPaid(selectedTemplate)"
          >
            <v-icon left>mdi-check-circle</v-icon>
            Guardar como Procesada
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="detailsDialog = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de confirmación para eliminar -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">
          Confirmar Eliminación
        </v-card-title>
        <v-card-text>
          ¿Está seguro que desea eliminar la plantilla "{{ selectedTemplate?.Name }}"?
          Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="deleteDialog = false">
            Cancelar
          </v-btn>
          <v-btn color="error" @click="confirmDelete" :loading="deleting">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de confirmación para marcar como procesada -->
    <v-dialog v-model="confirmPaidDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">
          Confirmar Procesamiento
        </v-card-title>
        <v-card-text>
          <p>¿Está seguro que desea marcar la plantilla <strong>"{{ selectedTemplate?.Name || selectedTemplate?.PlanNumber }}"</strong> como procesada?</p>
          <v-alert type="warning" dense text class="mt-3">
            <strong>Advertencia:</strong> Una vez marcada como procesada:
            <ul class="mt-2">
              <li>No podrá editar los montos de los pagos</li>
              <li>Esta acción no se puede deshacer</li>
            </ul>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="confirmPaidDialog = false">
            Cancelar
          </v-btn>
          <v-btn color="success" @click="confirmMarkAsPaid" :loading="processingPayment">
            <v-icon left>mdi-check-circle</v-icon>
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Alert -->
    <alerts
      v-if="alert.show"
      v-on:close_alert="closeAlert"
      v-on:accept_alert="acceptAlert"
      :properties="alert"
    ></alerts>
  </v-card>
</template>

<script>
import alerts from '@/components/Alerts.vue';
import { printToExcel } from '@/helpers/printToexcel';
import { getCookie } from '../../../helpers/misc';

export default {
  name: 'ListPaymentTemplates',
  components: {
    alerts
  },
  props: {
    win: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      loadingPayments: false,
      deleting: false,
      templates: [],
      selectedTemplate: null,
      templatePayments: [],
      templatePaymentsBackup: null,
      detailsDialog: false,
      deleteDialog: false,
      confirmPaidDialog: false,
      processingPayment: false,
      generatingAccountingEntry: false,
      editMode: false,
      modifyDate: false,
      editableTemplateDate: '',
      savingChanges: false,
      selectedAccountingMode: 'general',
      accountingModeOptions: [
        { text: 'Por cuenta general', value: 'general' },
        { text: 'Por cuenta de proveedores individual', value: 'provider' }
      ],
      headers: [
        { text: 'ID', value: 'ID', sortable: true },
        { text: 'Número de Plan', value: 'PlanNumber', sortable: true },
        { text: 'Número de Envío', value: 'SendNumber', sortable: true },
        { text: 'Fecha', value: 'Date', sortable: true },
        { text: 'Nombre', value: 'Name', sortable: true },
        { text: 'Total', value: 'TotalAmount', sortable: true, align: 'end' },
        { text: 'Cantidad', value: 'PaymentCount', sortable: true },
        { text: 'Estado', value: 'StatusPayment', sortable: true },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
      ],
      paymentHeaders: [
        { text: 'Tipo', value: 'TypeDocument', width: '80px' },
        { text: 'Número', value: 'DocumentNumber', width: '150px' },
        { text: 'Proveedor', value: 'ProviderName' },
        { text: 'Total Original', value: 'Total', align: 'end', width: '130px' },
        { text: 'Monto a Pagar', value: 'PartialAmount', align: 'end', width: '150px' },
        { text: 'Saldo', value: 'Balance', align: 'end', width: '120px' }
      ],
      alert: {
        type: 'success',
        show: false,
        header: '',
        body: ''
      }
    };
  },
  computed: {
    ID_Business() {
      return JSON.parse(localStorage.getItem('user')).businessid || 0;
    },
    ID_Branch() {
      return JSON.parse(localStorage.getItem('user')).branch || 0;
    }
  },
  mounted() {
    this.loadTemplates();
  },
  methods: {
    printToExcel,
    
    formatSendNumber(sendNumber) {
      if (!sendNumber && sendNumber !== 0) return 'N/A';
      return String(sendNumber).padStart(5, '0');
    },
    
    closeAlert() {
      this.alert.show = false;
    },
    acceptAlert() {
      this.alert.show = false;
    },
    showAlert(type, header, body, options = null) {
      type = type == null ? 'danger' : type;
      this.alert.type = type;
      this.alert.header = header;
      this.alert.body = body;
      this.alert.show = true;
      this.alert.options = options != null ? options : null;
    },
    async loadTemplates() {
      this.loading = true;
      try {
        const response = await this.$API.paymenttemplate.getAllTemplates();
        this.templates = response;
      } catch (error) {
        console.error('Error al cargar plantillas:', error);
        this.showAlert('danger', 'Error', 'Error al cargar las plantillas. Intente nuevamente.');
        this.templates = [];
      } finally {
        this.loading = false;
      }
    },
    
    async viewTemplate(template) {
      this.selectedTemplate = template;
      this.selectedAccountingMode = template.accounting_entry_mode || 'general';
      this.detailsDialog = true;
      this.loadingPayments = true;
      this.editMode = false;
      this.modifyDate = false;
      this.syncEditableTemplateDate();
      this.templatePaymentsBackup = null;
      
      try {
        const response = await this.$API.paymenttemplate.getTemplatePayments(template.ID);
        this.templatePayments = response;
      } catch (error) {
        console.error('Error al cargar detalles:', error);
        this.showAlert('danger', 'Error', 'Error al cargar los detalles de la plantilla.');
        this.templatePayments = [];
      } finally {
        this.loadingPayments = false;
      }
    },
    
    useTemplate(template) {
      // Aquí puedes implementar la lógica para usar la plantilla
      // Por ejemplo, navegar a una vista de procesamiento de pagos
      this.showAlert('info', 'Información', `Procesando plantilla: ${template.Name}`);
      this.$emit('use-template', template);
    },

    async generatePNR(template) {
      try {
        this.loading = true;
        
        // Llamar al backend para generar y descargar el archivo
        const result = await this.$API.paymenttemplate.downloadPNRFile(template.ID);
        
        this.showAlert('success', 'Éxito', `Archivo ${result.filename} descargado exitosamente`);
      } catch (error) {
        console.error('Error al generar archivo PRN:', error);
        this.showAlert('danger', 'Error', 'Error al generar el archivo PRN. Intente nuevamente.');
      } finally {
        this.loading = false;
      }
    },

    async exportToExcel(template) {
      try {
        this.loading = true;
        
        // Obtener los datos con información del proveedor (mismo que usa PNR)
        const data = await this.$API.paymenttemplate.getTemplatePaymentsWithProvider(template.ID);
        const payments = data.payments;
        const templateData = data.template;
        
        // Importar librerías
        const { Workbook } = await import('exceljs');
        const { saveAs } = await import('file-saver-es');
        
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('ACH');
        
        // Calcular totales usando montos parciales
        const totalAmount = payments.reduce((sum, p) => {
          const partialAmount = p.PartialAmount ? parseFloat(p.PartialAmount) : parseFloat(p.Total || 0);
          return sum + partialAmount;
        }, 0);
        const totalTransactions = payments.length;
        
        // Los datos de fecha ya vienen formateados del backend
        const year = payments[0]?.Year || new Date().getFullYear();
        const month = payments[0]?.Month || String(new Date().getMonth() + 1).padStart(2, '0');
        const day = payments[0]?.Day || String(new Date().getDate()).padStart(2, '0');
        
        // Línea de encabezado (SIN títulos de columna) - IGUAL QUE PNR
        worksheet.addRow([
          'B',  // Tipo de registro
          templateData.PlanNumber,  // # de Plan
          String(templateData.SendNumber).padStart(5, '0'),  // # de envío
          '',  // # de cuenta completo (vacío en encabezado)
          '',  // # de transacción (vacío en encabezado)
          year,  // Año
          month,  // Mes (con ceros)
          day,  // Día (con ceros)
          Math.round(totalAmount * 100),  // Total a pagar (sin decimales)
          totalTransactions,  // Total de transacciones (con espacios)
          '',  // Concepto (vacío en encabezado)
          '',  // Espacio en blanco para error
          '',  // Nombre (vacío en encabezado)
          '',  // Código de banco (vacío en encabezado)
          '',  // Transacción ACH (vacío en encabezado)
          '',  // Identificación (vacío en encabezado)
          '',  // Referencia (vacío en encabezado)
          ''   // Espacios
        ]);
        
        const keyMasterSpecialConcat = [
          `fa6646a6d6ef0c5f347a240270f49f38`
        ]

        const keyMastersArray = JSON.parse( getCookie("ID_KeyMaster") || "[]" );

        const keyMasterMatches = keyMastersArray.some(key => keyMasterSpecialConcat.includes(key));
        // Líneas de transacciones - USANDO DATOS FORMATEADOS DEL BACKEND
        payments.forEach((payment) => {
          worksheet.addRow([
            'T',  // Tipo de registro (T para transacciones)
            templateData.PlanNumber,  // # de Plan
            String(templateData.SendNumber).padStart(5, '0'),  // # de envío
            payment.ProviderBankAccount || '',  // # de cuenta completo del proveedor
            payment.TransactionNumber,  // # de transacción correlativo
            payment.Year,  // Año
            payment.Month,  // Mes (con ceros)
            payment.Day,  // Día (con ceros)
            Math.round((parseFloat(payment.PartialAmount) || parseFloat(payment.Total || 0)) * 100),  // Total a pagar (sin decimales)
            '',  // # correlativo de transacciones (vacío)
            ("plan " + (payment.Concept || '') + (keyMasterMatches ? ' POLICLINICA VETERINA' : '')).substring(0, 30),  // Concepto (Número de plan + Descripción)
            '',  // Espacio en blanco para error
            payment.ProviderName || '',  // Nombre del beneficiario/proveedor
            payment.ProviderBank || '',  // Transacción ACH / Código de banco del proveedor
            '25',  // Código de banco destino (valor fijo 22)
            payment.FormattedNumeroControl || '',  // Número de control DTE formateado (sin guiones)
            payment.ProviderLegalNITWithoutDashes || '',  // Identificación del proveedor NIT (sin guiones)
            ''  // Espacios
          ]);
        });

        worksheet.getColumn(15).alignment = { horizontal: 'right' };
        
        // Generar el nombre del archivo
        const planNum = String(templateData.PlanNumber).padStart(4, '0');
        const sendNum = String(templateData.SendNumber).padStart(5, '0');
        const fileName = `ACH_${planNum}_${sendNum}.xlsx`;
        
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
        
        this.showAlert('success', 'Éxito', `Archivo Excel descargado exitosamente`);
      } catch (error) {
        console.error('Error al exportar a Excel:', error);
        this.showAlert('danger', 'Error', 'Error al exportar a Excel. Intente nuevamente.');
      } finally {
        this.loading = false;
      }
    },
    
    deleteTemplate(template) {
      this.selectedTemplate = template;
      this.deleteDialog = true;
    },
    
    async confirmDelete() {
      this.deleting = true;
      try {
        await this.$API.paymenttemplate.deleteTemplate(this.selectedTemplate.ID);
        
        this.showAlert('success', 'Éxito', 'Plantilla eliminada exitosamente');
        this.deleteDialog = false;
        this.selectedTemplate = null;
        await this.loadTemplates();
      } catch (error) {
        console.error('Error al eliminar plantilla:', error);
        this.showAlert('danger', 'Error', 'Error al eliminar la plantilla. Intente nuevamente.');
      } finally {
        this.deleting = false;
      }
    },
    
    enableEditMode() {
      this.editMode = true;
      this.modifyDate = false;
      this.syncEditableTemplateDate();
      // Guardar copia de seguridad en caso de cancelación
      this.templatePaymentsBackup = JSON.parse(JSON.stringify(this.templatePayments));
      
      // Inicializar PartialAmount si está null
      this.templatePayments.forEach(payment => {
        if (!payment.PartialAmount) {
          payment.PartialAmount = parseFloat(payment.Total || 0);
        }
      });
    },
    
    cancelEdit() {
      // Restaurar datos originales
      if (this.templatePaymentsBackup) {
        this.templatePayments = JSON.parse(JSON.stringify(this.templatePaymentsBackup));
      }
      this.editMode = false;
      this.modifyDate = false;
      this.syncEditableTemplateDate();
      this.templatePaymentsBackup = null;
    },
    
    updatePaymentBalance(payment) {
      // Validar que el monto no exceda el total
      const total = parseFloat(payment.Total || 0);
      const partial = parseFloat(payment.PartialAmount || 0);
      
      if (partial > total) {
        payment.PartialAmount = total;
      }
      
      if (partial < 0) {
        payment.PartialAmount = 0;
      }
    },
    
    calculatePaymentBalance(payment) {
      const total = parseFloat(payment.Total || 0);
      const partial = parseFloat(payment.PartialAmount) || total;
      return total - partial;
    },
    
    getTemplateDateInput(date) {
      if (!date) return '';

      if (typeof date === 'string') {
        const trimmed = date.trim();
        if (!trimmed) return '';
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
        return trimmed.length >= 10 ? trimmed.substring(0, 10) : trimmed;
      }

      const parsedDate = new Date(date);
      if (Number.isNaN(parsedDate.getTime())) return '';
      return parsedDate.toISOString().slice(0, 10);
    },

    syncEditableTemplateDate() {
      this.editableTemplateDate = this.getTemplateDateInput(this.selectedTemplate && this.selectedTemplate.Date);
    },

    getPaymentBalanceClass(payment) {
      const balance = this.calculatePaymentBalance(payment);
      if (balance === 0) return 'success--text';
      if (balance > 0) return 'orange--text';
      return '';
    },
    
    getTemplateOriginalTotal() {
      return this.templatePayments.reduce((sum, p) => sum + parseFloat(p.Total || 0), 0);
    },
    
    getTemplatePartialTotal() {
      return this.templatePayments.reduce((sum, p) => {
        const partial = parseFloat(p.PartialAmount);
        return sum + (isNaN(partial) ? parseFloat(p.Total || 0) : partial);
      }, 0);
    },
    
    getTemplateBalanceTotal() {
      return this.getTemplateOriginalTotal() - this.getTemplatePartialTotal();
    },
    
    async saveChanges() {
      this.savingChanges = true;
      try {
        const originalDate = this.getTemplateDateInput(this.selectedTemplate && this.selectedTemplate.Date);
        const updatedDate = this.getTemplateDateInput(this.editableTemplateDate);


        const shouldUpdateDate = this.modifyDate && updatedDate && updatedDate !== originalDate;

        if (this.modifyDate && !updatedDate) {
          this.showAlert('warning', 'Advertencia', 'Seleccione una fecha válida antes de guardar.');
          return;
        }

        if (shouldUpdateDate) {
          const normalizedPayments = this.templatePayments.map(payment => ({
            ...payment,
            PartialAmount: parseFloat(payment.PartialAmount) || parseFloat(payment.Total || 0)
          }));

          const dateUpdatePayload = {
            name: this.selectedTemplate.Name || null,
            description: this.selectedTemplate.Description || null,
            templateDate: updatedDate,
            sendNumber: this.selectedTemplate.SendNumber != null
              ? String(this.selectedTemplate.SendNumber).padStart(5, '0')
              : null,
            payments: normalizedPayments,
            deletedDetailIds: []
          };

          await this.$API.paymenttemplate.updateTemplate(this.selectedTemplate.ID, dateUpdatePayload);
          this.selectedTemplate.Date = updatedDate;
        }

        // Preparar datos para enviar
        const updates = this.templatePayments.map(payment => ({
          ID: payment.ID,
          PartialAmount: parseFloat(payment.PartialAmount) || parseFloat(payment.Total || 0)
        }));
        
        const response = await this.$API.paymenttemplate.updatePartialAmounts(
          this.selectedTemplate.ID,
          updates
        );
        
        this.showAlert('success', 'Éxito', shouldUpdateDate ? 'Fecha y montos actualizados exitosamente' : 'Montos actualizados exitosamente');
        this.editMode = false;
        this.modifyDate = false;
        this.editableTemplateDate = '';
        this.templatePaymentsBackup = null;
        
        // Actualizar el total en el template seleccionado
        if (response.newTotal) {
          this.selectedTemplate.TotalAmount = response.newTotal;
        }
        
        // Recargar plantillas para actualizar el total en la lista
        await this.loadTemplates();
        
        // Recargar detalles para reflejar cambios
        await this.viewTemplate(this.selectedTemplate);
      } catch (error) {
        console.error('Error al guardar cambios:', error);
        this.showAlert('danger', 'Error', 'Error al guardar los cambios. Intente nuevamente.');
      } finally {
        this.savingChanges = false;
      }
    },
    formatDate(date) {
      if (!date) return '';
      const [year, month, day] = date.split('-');
      const d = new Date(year, month - 1, day);
      return d.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    },
    
    formatDateTime(datetime) {
      if (!datetime) return '';
      const d = new Date(datetime);
      return d.toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    formatInvoiceStatus(status) {
      if (status === null || status === undefined || status === '') {
        return 'Sin estado';
      }

      const normalized = String(status).trim().toUpperCase();
      const statusMap = {
        CANCELADA: 'Cancelada',
        CANCELADO: 'Cancelado',
        CREDITO: 'Crédito',
        'CRÉDITO': 'Crédito',
        ACREDITADO: 'Acreditado',
        ABONO: 'Abono',
        INVALIDADO: 'Invalidado',
        EMITIDO: 'Emitido',
        CONTADO: 'Contado',
        ANTICIPO: 'Anticipo',
        'NOTA DE CREDITO': 'Nota de crédito',
        'NOTA DE CRÉDITO': 'Nota de crédito',
        'NOTA DE DEBITO': 'Nota de débito',
        'NOTA DE DÉBITO': 'Nota de débito'
      };

      return statusMap[normalized] || String(status);
    },
    getInvoiceStatusColor(status) {
      const normalized = String(status || '').trim().toUpperCase();

      if (['CANCELADA', 'CANCELADO', 'CONTADO', 'EMITIDO'].includes(normalized)) {
        return 'success';
      }

      if (['CREDITO', 'CRÉDITO', 'ACREDITADO', 'ABONO', 'ANTICIPO'].includes(normalized)) {
        return 'primary';
      }

      if (['INVALIDADO', 'NOTA DE CREDITO', 'NOTA DE CRÉDITO', 'NOTA DE DEBITO', 'NOTA DE DÉBITO'].includes(normalized)) {
        return 'error';
      }

      return 'grey';
    },

    async markTemplateAsPaid(template) {
      this.selectedTemplate = template;
      this.confirmPaidDialog = true;
    },

    async confirmMarkAsPaid() {
      try {
        this.processingPayment = true;
        await this.$API.paymenttemplate.markAsPaid(this.selectedTemplate.ID);
        
        this.showAlert('success', 'Éxito', 'Plantilla marcada como procesada exitosamente');
        
        // Actualizar el estado en la lista local
        this.selectedTemplate.StatusPayment = 1;
        
        // Salir del modo edición si está activo
        this.editMode = false;
        this.modifyDate = false;
        this.editableTemplateDate = '';
        this.templatePaymentsBackup = null;
        
        // Cerrar el diálogo de confirmación
        this.confirmPaidDialog = false;
        
        // Recargar plantillas para refrescar la interfaz
        await this.loadTemplates();
      } catch (error) {
        console.error('Error al marcar plantilla como procesada:', error);
        this.showAlert('danger', 'Error', 'Error al actualizar el estado de la plantilla. Intente nuevamente.');
      } finally {
        this.processingPayment = false;
      }
    },

    openAccountingPreview() {
      if (!this.selectedTemplate?.ID) {
        this.showAlert('warning', 'Advertencia', 'Seleccione una plantilla válida.');
        return;
      }

      if (this.selectedTemplate.StatusPayment != 1) {
        this.showAlert('warning', 'Advertencia', 'La plantilla debe estar procesada antes de generar su partida contable.');
        return;
      }

      this.$emit('open-accounting-preview', {
        templateId: this.selectedTemplate.ID,
        accountingMode: this.selectedAccountingMode
      });
      this.detailsDialog = false;
      /*
      try {

        this.selectedTemplate.id_accounting_entry = response.data.id_accounting_entry;
        this.selectedTemplate.ID_Accounting_item = response.data.ID_Accounting_item;
        this.selectedTemplate.accounting_entry_mode = response.data.accounting_entry_mode;

        const templateIndex = this.templates.findIndex(item => item.ID === this.selectedTemplate.ID);
        if (templateIndex >= 0) {
          this.templates.splice(templateIndex, 1, {
            ...this.templates[templateIndex],
            id_accounting_entry: response.data.id_accounting_entry,
            ID_Accounting_item: response.data.ID_Accounting_item,
            accounting_entry_mode: response.data.accounting_entry_mode
          });
        }

        this.showAlert(
          'success',
          'Éxito',
          `Partida contable generada correctamente: ${response.data.ID_Accounting_item}`
        );

        this.$emit('open-accounting-preview', {
          templateId: this.selectedTemplate.ID,
          accountingMode: response.data.accounting_entry_mode || this.selectedAccountingMode
        });
        this.detailsDialog = false;
        await this.loadTemplates();
      } catch (error) {
        console.error('Error al generar partida contable:', error);
        const message = error?.response?.data?.message || 'No se pudo generar la partida contable.';
        this.showAlert('danger', 'Error', message);
      } finally {
        this.generatingAccountingEntry = false;
      }
      */
    },
    openAccountingEntryDetail() {
      if (!this.selectedTemplate?.id_accounting_entry) {
        this.showAlert('warning', 'Advertencia', 'La plantilla no tiene una partida contable asociada.');
        return;
      }

      this.detailsDialog = false;
      this.$store.dispatch('addWindow', {
        name: 'Acounting',
        component: 'Acounting',
        unique: false,
        meta: {
          accountingEntryToModify: {
            id_accounting_entry: this.selectedTemplate.id_accounting_entry,
            ID_Accounting_item: this.selectedTemplate.ID_Accounting_item
          }
        }
      });
    }
  }
};
</script>

<style scoped>
.headline {
  color: #1976d2;
}

/* Estilos para campos de monto parcial en modo edición */
::v-deep .v-text-field--outlined {
  border-radius: 4px;
}

::v-deep .v-text-field input {
  text-align: right;
}

/* Mejorar visualización de los totales */
.text-h6 {
  font-weight: 600;
}

.text-subtitle-2 {
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.payment-edit-actions {
  gap: 8px;
}

.payment-date-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.payment-date-content {
  display: flex;
  align-items: center;
  min-width: 0;
}

.payment-date-value {
  font-size: 0.875rem;
  line-height: 1.5;
}

.payment-date-input {
  max-width: 180px;
}

::v-deep .payment-edit-checkbox {
  margin-top: 0;
  padding-top: 0;
}

::v-deep .payment-edit-checkbox .v-label {
  font-size: 0.875rem;
}


::v-deep .payment-dialog__card {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

::v-deep .payment-dialog__card .v-card__text {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
/* Asegurar que el diálogo sea lo suficientemente grande */
::v-deep .v-dialog {
  overflow-y: auto;
}
</style>
